import { TransactionResponse } from "@common/dto/transactions/update-transaction-response.dto";
import { MoneyDirection } from "@common/constants";
import { Id } from "react-toastify/unstyled";
import { TransactionFiltersDto } from "@common/dto/transactions/getTransactions/transaction-filters.dto";
import { UpdateTransactionDto } from "@common/dto/transactions/update-transaction.dto";
import {
    BulkUpdateTransactionsRequestInput,
    TransactionListItem,
} from "@common/schemas/transaction.schema";
import { Transaction } from "@common/types/transaction";
import { TransactionView } from "@common/types/views/transactionViews";
import { getTransactions } from "@api/transaction.api";
import { SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY } from "../counterparty/SimilarTransactionsUpdateToast/SimilarTransactionsUpdateToast.lib";
import { SimilarTransactionsUpdateToast } from "../counterparty/SimilarTransactionsUpdateToast/SimilarTransactionsUpdateToast";
import {
    BulkUpdateDetails,
    BulkUpdateParams,
    getScheduleEntityLockFilters,
} from "./TransactionsBulkActions/lib";
import { BulkUpdateResult } from "./PaginatedTransactionsTable.context";
import { SelectedTransactionBase } from "./TransactionsBulkActions/transactionsBulkActionsContext";

const LIMIT_TO_CHECK = 20;

export interface SimilarTransactionsFilterTransaction {
    id: number;
    amount: number;
    entity: { id: number };
}

function transactionMatchesBulkUpdateValue(
    transaction: TransactionListItem,
    params: BulkUpdateParams,
): boolean {
    if (params.update === "counterpartyId") {
        return transaction.counterpartyId === params.value;
    }
    if (params.update === "categoryId") {
        return transaction.categoryId === params.value;
    }
    if (params.update === "coa") {
        return transaction.accountId === params.value;
    }
    if (params.update === "accrualCoa") {
        return transaction.accrualAccountId === params.value;
    }
    if (params.update === "classes") {
        return (
            transaction.classAssignments?.length === params.value.length &&
            transaction.classAssignments?.every((classAssignment) =>
                params.value.includes(classAssignment.classId),
            )
        );
    }
    if (params.update === "entity") {
        return transaction.entity.id === params.value.id;
    }
    return false;
}

function getDirectionFromAmount(amount: number): MoneyDirection {
    return amount > 0 ? MoneyDirection.MONEY_IN : MoneyDirection.MONEY_OUT;
}

function resolveCommonDirection(
    transactions: SimilarTransactionsFilterTransaction[],
): MoneyDirection | undefined {
    if (transactions.length === 0) {
        return undefined;
    }
    const firstDirection = getDirectionFromAmount(transactions[0].amount);
    const isUniform = transactions.every(
        (t) => getDirectionFromAmount(t.amount) === firstDirection,
    );
    return isUniform ? firstDirection : undefined;
}

export function buildSimilarTransactionsFiltersForUpdatedTransaction({
    normalizedDescriptionId,
    payload,
    transactionAfterUpdate,
    transactionBeforeUpdate,
}: {
    normalizedDescriptionId: string;
    payload: UpdateTransactionDto;
    transactionAfterUpdate: SimilarTransactionsFilterTransaction;
    transactionBeforeUpdate: SimilarTransactionsFilterTransaction;
}): TransactionFiltersDto {
    let filterEntityId = transactionAfterUpdate.entity.id;
    if (payload.entityId !== undefined) {
        filterEntityId = transactionBeforeUpdate.entity.id;
    }

    return {
        entityIds: [filterEntityId],
        normalizedDescriptionId,
        direction: getDirectionFromAmount(transactionAfterUpdate.amount),
        excludedTransactionIds: [transactionAfterUpdate.id],
        ...(payload.entityId !== undefined
            ? getScheduleEntityLockFilters("entity")
            : {}),
    };
}

export function buildSimilarTransactionsFiltersForBulkUpdate({
    canonicalNormalizedDescriptionId,
    update,
    updatedTransactions,
    transactionsBeforeUpdate,
}: {
    canonicalNormalizedDescriptionId: string;
    update: BulkUpdateParams["update"];
    updatedTransactions: SimilarTransactionsFilterTransaction[];
    transactionsBeforeUpdate: SimilarTransactionsFilterTransaction[];
}): TransactionFiltersDto {
    const transactionsForEntityScope =
        update === "entity" ? transactionsBeforeUpdate : updatedTransactions;
    const entityIds = [
        ...new Set(transactionsForEntityScope.map((t) => t.entity.id)),
    ];
    const excludedTransactionIds = updatedTransactions.map((t) => t.id);
    const direction = resolveCommonDirection(updatedTransactions);

    return {
        entityIds,
        normalizedDescriptionId: canonicalNormalizedDescriptionId,
        excludedTransactionIds,
        ...(direction ? { direction } : {}),
        ...getScheduleEntityLockFilters(update),
    };
}

export async function checkSimilarTransactionsAndShowToastIfApplicable({
    workspaceId,
    response,
    payload,
    transactionBeforeUpdate,
    toast,
    doUpdate,
    undo,
    displayedChangedEntityName,
    view,
}: {
    workspaceId: string;
    response: TransactionResponse;
    payload: UpdateTransactionDto;
    transactionBeforeUpdate: TransactionListItem;
    displayedChangedEntityName?: string;
    view: TransactionView;
    toast: (
        content: React.ReactNode,
        key?: Id,
        options?: {
            preventAutoClose?: boolean;
            onClose?: () => void;
        },
    ) => Id;
    doUpdate?: (
        details: BulkUpdateDetails,
        payloads: BulkUpdateTransactionsRequestInput[],
    ) => Promise<{
        updatedTransactions: Transaction[];
        details: BulkUpdateDetails;
        transactionsBeforeUpdate: Transaction[];
    }>;
    undo?: (params: {
        previousTransactions: Record<number, SelectedTransactionBase>;
        updateDetailsForUndo: BulkUpdateDetails;
    }) => unknown;
}): Promise<boolean> {
    const normalizedDescriptionId =
        response.transaction.normalizedDescriptionId;
    if (!normalizedDescriptionId) {
        return false;
    }

    const nonNullAccountOverrideEntries =
        payload.accountOverrides?.filter((entry) => entry.accountId !== null) ??
        [];
    const accountOverrideUpdate =
        nonNullAccountOverrideEntries.length === 1
            ? nonNullAccountOverrideEntries[0]
            : undefined;
    let accountOverrideBasis: "cash" | "accrual" | undefined;
    if (accountOverrideUpdate?.accountId === response.transaction.accountId) {
        accountOverrideBasis = "cash";
    } else if (
        accountOverrideUpdate?.accountId ===
        response.transaction.accrualAccountId
    ) {
        accountOverrideBasis = "accrual";
    }
    const populatedUpdateKindCount = [
        payload.counterpartyId !== undefined &&
            payload.counterpartyId !== null &&
            !!response.counterpartyChangedContext,
        !!payload.categoryId,
        !!accountOverrideBasis,
        payload.entityId !== undefined,
    ].filter(Boolean).length;
    if (populatedUpdateKindCount > 1) {
        return false;
    }

    const filters = buildSimilarTransactionsFiltersForUpdatedTransaction({
        normalizedDescriptionId,
        payload,
        transactionAfterUpdate: response.transaction,
        transactionBeforeUpdate,
    });
    let valueToAssign: BulkUpdateParams | undefined;

    if (
        payload.counterpartyId !== undefined &&
        payload.counterpartyId !== null &&
        response.counterpartyChangedContext
    ) {
        valueToAssign = {
            update: "counterpartyId",
            value: payload.counterpartyId,
            counterpartyName:
                response.counterpartyChangedContext.counterpartyName,
        };
    }
    if (payload.categoryId) {
        valueToAssign = {
            update: "categoryId",
            value: payload.categoryId,
        };
    }
    if (accountOverrideUpdate && accountOverrideBasis) {
        valueToAssign =
            accountOverrideBasis === "accrual"
                ? {
                      update: "accrualCoa",
                      value: accountOverrideUpdate.accountId,
                      accountName:
                          displayedChangedEntityName ??
                          response.transaction.accrualAccount?.name,
                  }
                : {
                      update: "coa",
                      value: accountOverrideUpdate.accountId,
                      accountName:
                          displayedChangedEntityName ??
                          response.transaction.account?.name,
                  };
    }
    if (payload.entityId !== undefined) {
        valueToAssign = {
            update: "entity",
            value: response.transaction.entity,
        };
    }

    if (!valueToAssign) {
        return false;
    }

    const transactionsWithSameNormalizedDescription = await getTransactions({
        filters,
        page: 0,
        limit: LIMIT_TO_CHECK,
        workspaceId,
    });

    const allTransactionsHaveTheSameValueToAssign =
        transactionsWithSameNormalizedDescription.data.every((transaction) =>
            transactionMatchesBulkUpdateValue(transaction, valueToAssign),
        );

    if (
        transactionsWithSameNormalizedDescription.data.length > 1 &&
        (!allTransactionsHaveTheSameValueToAssign ||
            transactionsWithSameNormalizedDescription.data.length >=
                LIMIT_TO_CHECK)
    ) {
        const passedDisplayedChangedEntityName = (() => {
            if (valueToAssign.update === "counterpartyId") {
                return response.counterpartyChangedContext?.counterpartyName;
            }
            if (valueToAssign.update === "coa") {
                return displayedChangedEntityName;
            }
            return displayedChangedEntityName;
        })();

        toast(
            <SimilarTransactionsUpdateToast
                totalTransactions={
                    transactionsWithSameNormalizedDescription.total
                }
                valueToAssign={valueToAssign}
                filters={filters}
                doUpdate={doUpdate}
                undo={undo}
                displayedChangedEntityName={passedDisplayedChangedEntityName}
                view={view}
                updatedTransaction={response.transaction}
            />,
            SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY,
        );
        return true;
    }

    return false;
}

const SUPPORTED_BULK_UPDATE_TYPES = new Set<BulkUpdateParams["update"]>([
    "counterpartyId",
    "categoryId",
    "coa",
    "accrualCoa",
    "entity",
]);

export async function checkSimilarTransactionsForBulkUpdateAndShowToastIfApplicable({
    workspaceId,
    canonicalNormalizedDescriptionId,
    updatedTransactions,
    transactionsBeforeUpdate,
    params,
    toast,
    doUpdate,
    undo,
    displayedChangedEntityName,
    view,
}: {
    workspaceId: string;
    canonicalNormalizedDescriptionId: string | null;
    updatedTransactions: Transaction[];
    transactionsBeforeUpdate: Transaction[];
    params: BulkUpdateParams;
    displayedChangedEntityName?: string;
    view: TransactionView;
    toast: (
        content: React.ReactNode,
        key?: Id,
        options?: {
            preventAutoClose?: boolean;
            onClose?: () => void;
        },
    ) => Id;
    doUpdate: (
        details: BulkUpdateDetails,
        payloads: BulkUpdateTransactionsRequestInput[],
    ) => Promise<BulkUpdateResult>;
    undo: (params: {
        previousTransactions: Record<number, SelectedTransactionBase>;
        updateDetailsForUndo: BulkUpdateDetails;
    }) => unknown;
}) {
    if (!canonicalNormalizedDescriptionId) {
        return;
    }

    if (!SUPPORTED_BULK_UPDATE_TYPES.has(params.update)) {
        return;
    }

    const filters = buildSimilarTransactionsFiltersForBulkUpdate({
        canonicalNormalizedDescriptionId,
        update: params.update,
        updatedTransactions,
        transactionsBeforeUpdate,
    });

    const transactionsWithSameNormalizedDescription = await getTransactions({
        filters,
        page: 0,
        limit: LIMIT_TO_CHECK,
        workspaceId,
    });

    const allTransactionsHaveTheSameValueToAssign =
        transactionsWithSameNormalizedDescription.data.every((transaction) =>
            transactionMatchesBulkUpdateValue(transaction, params),
        );

    if (
        transactionsWithSameNormalizedDescription.data.length > 1 &&
        (!allTransactionsHaveTheSameValueToAssign ||
            transactionsWithSameNormalizedDescription.data.length >=
                LIMIT_TO_CHECK)
    ) {
        const representativeTransaction = updatedTransactions[0];

        toast(
            <SimilarTransactionsUpdateToast
                totalTransactions={
                    transactionsWithSameNormalizedDescription.total
                }
                valueToAssign={params}
                filters={filters}
                doUpdate={doUpdate}
                undo={undo}
                displayedChangedEntityName={displayedChangedEntityName}
                view={view}
                updatedTransaction={representativeTransaction}
            />,
            SIMILAR_TRANSACTIONS_UPDATE_TOAST_MESSAGE_KEY,
        );
    }
}
